import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X } from "lucide-react";
import { api, formatApiError } from "../../api";
import { TRAINER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { GymLoader } from "../../components/common/GymLoader";

function fmtDateTime(iso) {
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// UX-TR-01 §1 SCR-P06-10/12: every session row IS the roster (one row per attendee), plus each
// class schedule's waitlist attached inline (waitlist-promotion visibility). `attend`/`no-show`
// call the trainer-scoped write endpoints — a session that isn't this trainer's own gets a real
// 403 server-side (see `_require_own_booking`), never just a hidden button.
export default function Schedule() {
  const reducedMotion = useReducedMotion();
  const qc = useQueryClient();
  const scheduleQ = useQuery({ queryKey: ["trainer-schedule"], queryFn: async () => (await api.get("/trainer/schedule")).data });

  const attendMutation = useMutation({
    mutationFn: (bookingId) => api.post(`/trainer/bookings/${bookingId}/attend`),
    onSuccess: () => { toast.success("Marked attended."); qc.invalidateQueries({ queryKey: ["trainer-schedule"] }); qc.invalidateQueries({ queryKey: ["trainer-home"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const noShowMutation = useMutation({
    mutationFn: (bookingId) => api.post(`/trainer/bookings/${bookingId}/no-show`, { penalty_amount: 0 }),
    onSuccess: () => { toast.success("Marked no-show."); qc.invalidateQueries({ queryKey: ["trainer-schedule"] }); qc.invalidateQueries({ queryKey: ["trainer-home"] }); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const upcoming = useMemo(() => (scheduleQ.data?.sessions || []).filter((s) => s.status === "booked" || s.status === "waitlisted"), [scheduleQ.data]);
  const past = useMemo(() => (scheduleQ.data?.sessions || []).filter((s) => s.status === "attended" || s.status === "no_show"), [scheduleQ.data]);
  const waitlists = useMemo(() => (scheduleQ.data?.class_schedules || []).filter((c) => (c.waitlist || []).length > 0), [scheduleQ.data]);

  if (scheduleQ.isLoading) return <GymLoader fullPage label="Loading schedule…" testId="trainer-schedule-loader" />;

  return (
    <div data-testid={TRAINER.schedulePage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Schedule</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Your upcoming sessions and recent history.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>Upcoming</p>
        {upcoming.length === 0 && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>Nothing booked right now.</p>}
        {upcoming.map((s) => (
          <div key={s.id} data-testid={TRAINER.scheduleSessionRow(s.id)} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div>
              <p className="text-sm font-medium" style={{ color: "#141416" }}>{s.member_name || "—"}</p>
              <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{fmtDateTime(s.slot.start)} · {s.session_kind === "pt" ? "1:1 PT" : s.class_type} {s.status === "waitlisted" && "· waitlisted"}</p>
            </div>
            {s.status === "booked" && (
              <div className="flex gap-2">
                <Button data-testid={TRAINER.attendButton(s.id)} size="sm" disabled={attendMutation.isPending} onClick={() => attendMutation.mutate(s.id)} className="text-white gap-1" style={{ background: "var(--scarlet-pos)" }}>
                  <Check size={13} /> Attend
                </Button>
                <Button data-testid={TRAINER.noShowButton(s.id)} size="sm" variant="outline" disabled={noShowMutation.isPending} onClick={() => noShowMutation.mutate(s.id)} className="gap-1">
                  <X size={13} /> No-show
                </Button>
              </div>
            )}
          </div>
        ))}
      </motion.div>

      {waitlists.length > 0 && (
        <motion.div {...fadeUp(reducedMotion, 0.06)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>Waitlists</p>
          {waitlists.map((c) => (
            <div key={c.id} className="px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
              <p className="text-xs font-semibold mb-1.5" style={{ color: "#68686E" }}>{c.class_type} · {fmtDateTime(c.start)}</p>
              {c.waitlist.map((w) => (
                <div key={w.member_ref} data-testid={TRAINER.waitlistRow(w.member_ref)} className="flex items-center justify-between text-sm py-1">
                  <span style={{ color: "#141416" }}>{w.member_name}</span>
                  <span style={{ color: "#68686E" }}>{w.promoted_at ? "promoted" : `#${w.position ?? "—"} in line`}</span>
                </div>
              ))}
            </div>
          ))}
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.1)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}>Recent history</p>
        {past.length === 0 && <p className="text-sm px-4 py-6" style={{ color: "#68686E" }}>No history yet.</p>}
        {past.slice(0, 20).map((s) => (
          <div key={s.id} className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div>
              <p className="text-sm font-medium" style={{ color: "#141416" }}>{s.member_name || "—"}</p>
              <p className="text-xs mt-0.5" style={{ color: "#68686E" }}>{fmtDateTime(s.slot.start)} · {s.session_kind === "pt" ? "1:1 PT" : s.class_type}</p>
            </div>
            <span className={`status-pill ${s.status === "attended" ? "active" : "overdue"}`}>{s.status}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
